import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ScrollText, ExternalLink, AlertTriangle, CheckCircle } from "lucide-react";

/**
 * Briefing Ledger — Owner-visible provenance dashboard for every
 * AI briefing generated across the tenant.
 *
 * Each row shows: when, who, prompt version, confidence, whether the
 * fallback fired, source count, and a deep-link to replay the exact
 * briefing. Answers "how do we know which version of the AI wrote
 * this?" in five clicks.
 */
const _fmtWhen = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
};

const _fmtConfidence = (c) => {
  if (typeof c !== "number") return "—";
  return `${(c * 100).toFixed(0)}%`;
};

export default function BriefingLedger() {
  const [data, setData] = useState(null);
  const [insufficientOnly, setInsufficientOnly] = useState(false);
  const [skip, setSkip] = useState(0);
  const [inspecting, setInspecting] = useState(null);
  const [inspectData, setInspectData] = useState(null);
  const LIMIT = 25;

  const load = async () => {
    try {
      const { data } = await api.get("/exec/ai-briefing/ledger", {
        params: { limit: LIMIT, skip, insufficient_only: insufficientOnly },
      });
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load ledger.");
    }
  };

  useEffect(() => { load(); }, [insufficientOnly, skip]);

  // Prompt inspector — fetch the exact prompt text on demand.
  useEffect(() => {
    if (!inspecting) { setInspectData(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/exec/prompts/${inspecting.key}/${inspecting.version}`);
        setInspectData(data);
      } catch (err) {
        if (err.response?.status === 404) {
          setInspectData({ retired: true });
        } else {
          toast.error("Could not load prompt.");
          setInspecting(null);
        }
      }
    })();
  }, [inspecting]);

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="briefing-ledger-page">
      <header className="mb-6">
        <p className="ek-eyebrow flex items-center gap-1.5">
          <ScrollText size={12} /> AI Audit Trail
        </p>
        <h1 className="mt-1 font-display text-2xl font-semibold text-ek-ink">
          Briefing Ledger
        </h1>
        <p className="mt-1.5 max-w-2xl text-sm text-ek-ink/65">
          Every AI briefing generated on your tenant, with the exact prompt version,
          confidence score, and grounded-source count captured at generation. Click a row
          to replay the exact briefing your reader saw.
        </p>
      </header>

      <div className="mb-4 flex items-center justify-between gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-ek-ink/75" data-testid="ledger-filter-insufficient">
          <input
            type="checkbox"
            checked={insufficientOnly}
            onChange={(e) => { setInsufficientOnly(e.target.checked); setSkip(0); }}
            className="h-4 w-4 rounded border-black/20 text-ek-purple focus:ring-ek-purple"
          />
          Show only insufficient-evidence refusals
        </label>
        {data && (
          <p className="text-xs text-ek-ink/50" data-testid="ledger-total">
            {data.total} briefing{data.total === 1 ? "" : "s"} total
          </p>
        )}
      </div>

      {data === null ? (
        <p className="text-sm text-ek-ink/55" data-testid="ledger-loading">Loading ledger…</p>
      ) : data.items.length === 0 ? (
        <div className="rounded-lg border border-black/5 bg-white p-8 text-center" data-testid="ledger-empty">
          <p className="text-sm text-ek-ink/65">
            No briefings recorded yet. Generate one from the dashboard.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-black/5 bg-white">
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead className="bg-ek-canvas text-[11px] font-semibold uppercase tracking-wide text-ek-ink/60">
              <tr>
                <th className="px-4 py-2.5 text-left">Generated</th>
                <th className="px-4 py-2.5 text-left">Author</th>
                <th className="px-4 py-2.5 text-left">Prompt</th>
                <th className="px-4 py-2.5 text-right">Confidence</th>
                <th className="px-4 py-2.5 text-right">Sources</th>
                <th className="px-4 py-2.5 text-center">Status</th>
                <th className="px-4 py-2.5 text-right">Replay</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {data.items.map((row) => (
                <tr key={row.briefing_id} data-testid={`ledger-row-${row.briefing_id}`}>
                  <td className="px-4 py-2.5 text-ek-ink/80">{_fmtWhen(row.generated_at)}</td>
                  <td className="px-4 py-2.5 text-ek-ink/80">
                    {row.author?.name || row.author?.email || row.author?.user_id || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-ek-ink/70">
                    {row.prompt_key ? (
                      <button
                        type="button"
                        onClick={() => setInspecting({ key: row.prompt_key, version: row.prompt_version })}
                        className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-left hover:bg-ek-purple/5"
                        data-testid={`ledger-inspect-${row.briefing_id}`}
                        title="Click to inspect the exact prompt text for this version"
                      >
                        <span className="font-mono text-[12px]">{row.prompt_key}</span>
                        {" "}
                        <span className="rounded bg-ek-purple/10 px-1.5 py-0.5 text-[10px] font-semibold text-ek-purple">
                          v{row.prompt_version}
                        </span>
                      </button>
                    ) : (
                      <span className="text-ek-ink/40">inline</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-[12px] text-ek-ink/80">
                    {_fmtConfidence(row.confidence)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-ek-ink/70">
                    {row.source_count}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {row.insufficient_data ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`ledger-status-insufficient-${row.briefing_id}`}>
                        <AlertTriangle size={11} /> Refused
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid={`ledger-status-ok-${row.briefing_id}`}>
                        <CheckCircle size={11} /> Generated
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Link
                      to={`/dashboard?briefing=${row.briefing_id}`}
                      className="inline-flex items-center gap-1 text-[12px] font-semibold text-ek-purple hover:underline"
                      data-testid={`ledger-replay-${row.briefing_id}`}
                    >
                      Replay <ExternalLink size={11} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (data.has_more || skip > 0) && (
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setSkip((s) => Math.max(0, s - LIMIT))}
            disabled={skip === 0}
            data-testid="ledger-prev"
            className="rounded-lg border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-ek-ink disabled:opacity-40"
          >
            ← Previous
          </button>
          <span className="text-xs text-ek-ink/55">
            {skip + 1}–{skip + data.items.length} of {data.total}
          </span>
          <button
            type="button"
            onClick={() => setSkip((s) => s + LIMIT)}
            disabled={!data.has_more}
            data-testid="ledger-next"
            className="rounded-lg border border-black/10 bg-white px-3 py-1.5 text-xs font-semibold text-ek-ink disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}

      {inspecting && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setInspecting(null)}
          data-testid="prompt-inspector-overlay"
        >
          <div
            className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="prompt-inspector"
          >
            <div className="flex items-start justify-between gap-3 border-b border-black/5 px-5 py-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-ek-ink/50">
                  Prompt inspector
                </p>
                <p className="mt-0.5 font-mono text-sm text-ek-ink">
                  {inspecting.key} <span className="rounded bg-ek-purple/10 px-1.5 py-0.5 text-[10px] font-semibold text-ek-purple">v{inspecting.version}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setInspecting(null)}
                data-testid="prompt-inspector-close"
                className="rounded-md p-1 text-ek-ink/50 hover:bg-ek-canvas hover:text-ek-ink"
                aria-label="Close prompt inspector"
              >
                <ExternalLink size={16} className="rotate-45" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              {!inspectData ? (
                <p className="text-sm text-ek-ink/55">Loading prompt…</p>
              ) : inspectData.retired ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="prompt-inspector-retired">
                  <p className="text-sm font-semibold text-amber-900">Archived / retired version</p>
                  <p className="mt-1 text-xs text-amber-900/75">
                    This briefing was generated before the prompt registry existed, or with a
                    prompt version that has since been removed from history. The exact text
                    is not preserved. Ask the platform team for the archived text if needed
                    for audit purposes.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-[11px] text-ek-ink/50">
                    Surface: <span className="font-mono">{inspectData.surface}</span> · Hash: <span className="font-mono">{inspectData.hash}</span>
                  </p>
                  <pre
                    className="mt-3 max-h-96 overflow-auto rounded-md bg-ek-canvas p-3 text-xs leading-relaxed text-ek-ink/85"
                    data-testid="prompt-inspector-text"
                  >
                    {inspectData.text}
                  </pre>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
